import type { Language } from '../types/i18n'

export type UiStrings = {
  nav: { cv: string; portfolio: string; contact: string }
  menuOpen: string
  menuClose: string
  languageLabel: string
  hero: { portfolioCta: string; contactCta: string; portraitAlt: string }
  sections: {
    about: string
    features: string
    skills: string
    experience: string
    education: string
    contact: string
    photos: string
    reels: string
    snippets: string
  }
  portfolio: {
    eyebrow: string
    title: string
    intro: string
    filterLabel: string
    reelsIntro: string
    closeLabel: string
  }
  form: {
    intro: string
    name: string
    email: string
    subject: string
    message: string
    submit: string
    sending: string
    successTitle: string
    successBody: string
    again: string
    errorRequired: string
    errorEmail: string
    errorSubmit: string
    basedIn: string
    linkedinCta: string
  }
}

export const ui: Record<Language, UiStrings> = {
  nl: {
    nav: { cv: 'C.V.', portfolio: 'Portfolio', contact: 'Contact' },
    menuOpen: 'Menu openen',
    menuClose: 'Menu sluiten',
    languageLabel: 'Taal kiezen',
    hero: {
      portfolioCta: 'Bekijk portfolio',
      contactCta: 'Neem contact op',
      portraitAlt: 'Portret van Fauve Willems',
    },
    sections: {
      about: 'Over mij',
      features: 'Kenmerken',
      skills: 'Vaardigheden',
      experience: 'Werkervaring',
      education: 'Opleiding',
      contact: 'Contact',
      photos: "Foto's",
      reels: 'Reels',
      snippets: 'Video snippets',
    },
    portfolio: {
      eyebrow: 'Portfolio',
      title: "Foto's, reels & video",
      intro:
        "Een selectie van casting foto's, dans- en zangreels en fragmenten uit producties. Voor volledige video's of specifiek materiaal voor een auditie stuur je gerust een bericht.",
      filterLabel: "Filter foto's",
      reelsIntro: 'Korte verticale reels voor casting: dans, zang en acteren.',
      closeLabel: 'Sluiten',
    },
    form: {
      intro:
        'Interesse in een samenwerking, auditie of workshop? Stuur me een e-mail of een bericht via LinkedIn of Instagram, dan reageer ik zo snel mogelijk.',
      name: 'Naam',
      email: 'E-mailadres',
      subject: 'Onderwerp',
      message: 'Bericht',
      submit: 'Verstuur bericht',
      sending: 'Versturen…',
      successTitle: 'Bedankt voor je bericht!',
      successBody: 'Ik neem zo snel mogelijk contact met je op.',
      again: 'Nog een bericht sturen',
      errorRequired: 'Dit veld is verplicht',
      errorEmail: 'Vul een geldig e-mailadres in',
      errorSubmit: 'Er ging iets mis. Probeer het opnieuw.',
      basedIn: 'Gevestigd in',
      linkedinCta: 'Neem contact op',
    },
  },

  en: {
    nav: { cv: 'C.V.', portfolio: 'Portfolio', contact: 'Contact' },
    menuOpen: 'Open menu',
    menuClose: 'Close menu',
    languageLabel: 'Choose language',
    hero: {
      portfolioCta: 'View portfolio',
      contactCta: 'Get in touch',
      portraitAlt: 'Portrait of Fauve Willems',
    },
    sections: {
      about: 'About me',
      features: 'Features',
      skills: 'Skills',
      experience: 'Work experience',
      education: 'Education',
      contact: 'Contact',
      photos: 'Photos',
      reels: 'Reels',
      snippets: 'Video snippets',
    },
    portfolio: {
      eyebrow: 'Portfolio',
      title: 'Photos, reels & video',
      intro:
        'A selection of casting photos, dance and vocal reels, and excerpts from productions. For full videos or specific material for an audition, send me a message.',
      filterLabel: 'Filter photos',
      reelsIntro: 'Short vertical reels for casting: dance, voice and acting.',
      closeLabel: 'Close',
    },
    form: {
      intro:
        'Interested in a collaboration, audition or workshop? Send me an email or a message on LinkedIn or Instagram and I will get back to you as soon as possible.',
      name: 'Name',
      email: 'Email address',
      subject: 'Subject',
      message: 'Message',
      submit: 'Send message',
      sending: 'Sending…',
      successTitle: 'Thank you for your message!',
      successBody: 'I will get back to you as soon as possible.',
      again: 'Send another message',
      errorRequired: 'This field is required',
      errorEmail: 'Please enter a valid email address',
      errorSubmit: 'Something went wrong. Please try again.',
      basedIn: 'Based in',
      linkedinCta: 'Get in touch',
    },
  },

  de: {
    nav: { cv: 'Lebenslauf', portfolio: 'Portfolio', contact: 'Kontakt' },
    menuOpen: 'Menü öffnen',
    menuClose: 'Menü schließen',
    languageLabel: 'Sprache wählen',
    hero: {
      portfolioCta: 'Portfolio ansehen',
      contactCta: 'Kontakt aufnehmen',
      portraitAlt: 'Porträt von Fauve Willems',
    },
    sections: {
      about: 'Über mich',
      features: 'Merkmale',
      skills: 'Fähigkeiten',
      experience: 'Berufserfahrung',
      education: 'Ausbildung',
      contact: 'Kontakt',
      photos: 'Fotos',
      reels: 'Reels',
      snippets: 'Video-Snippets',
    },
    portfolio: {
      eyebrow: 'Portfolio',
      title: 'Fotos, Reels & Video',
      intro:
        'Eine Auswahl an Casting-Fotos, Tanz- und Gesangs-Reels sowie Ausschnitten aus Produktionen. Für vollständige Videos oder spezielles Material für ein Vorsprechen schreiben Sie mir gerne.',
      filterLabel: 'Fotos filtern',
      reelsIntro: 'Kurze vertikale Reels fürs Casting: Tanz, Gesang und Schauspiel.',
      closeLabel: 'Schließen',
    },
    form: {
      intro:
        'Interesse an einer Zusammenarbeit, einem Vorsprechen oder einem Workshop? Schreiben Sie mir per E-Mail oder über LinkedIn oder Instagram – ich melde mich so schnell wie möglich zurück.',
      name: 'Name',
      email: 'E-Mail-Adresse',
      subject: 'Betreff',
      message: 'Nachricht',
      submit: 'Nachricht senden',
      sending: 'Wird gesendet…',
      successTitle: 'Vielen Dank für Ihre Nachricht!',
      successBody: 'Ich melde mich so schnell wie möglich bei Ihnen.',
      again: 'Weitere Nachricht senden',
      errorRequired: 'Dieses Feld ist erforderlich',
      errorEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      errorSubmit: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
      basedIn: 'Ansässig in',
      linkedinCta: 'Kontakt aufnehmen',
    },
  },

  fr: {
    nav: { cv: 'C.V.', portfolio: 'Portfolio', contact: 'Contact' },
    menuOpen: 'Ouvrir le menu',
    menuClose: 'Fermer le menu',
    languageLabel: 'Choisir la langue',
    hero: {
      portfolioCta: 'Voir le portfolio',
      contactCta: 'Me contacter',
      portraitAlt: 'Portrait de Fauve Willems',
    },
    sections: {
      about: 'À propos',
      features: 'Caractéristiques',
      skills: 'Compétences',
      experience: 'Expérience',
      education: 'Formation',
      contact: 'Contact',
      photos: 'Photos',
      reels: 'Reels',
      snippets: 'Extraits vidéo',
    },
    portfolio: {
      eyebrow: 'Portfolio',
      title: 'Photos, reels & vidéo',
      intro:
        "Une sélection de photos de casting, de reels de danse et de chant, ainsi que d'extraits de productions. Pour des vidéos complètes ou du matériel spécifique pour une audition, envoyez-moi un message.",
      filterLabel: 'Filtrer les photos',
      reelsIntro: 'Reels verticaux courts pour le casting : danse, chant et jeu.',
      closeLabel: 'Fermer',
    },
    form: {
      intro:
        'Envie de collaborer, d’organiser une audition ou un atelier ? Écrivez-moi par e-mail ou sur LinkedIn ou Instagram, je réponds dès que possible.',
      name: 'Nom',
      email: 'Adresse e-mail',
      subject: 'Objet',
      message: 'Message',
      submit: 'Envoyer le message',
      sending: 'Envoi…',
      successTitle: 'Merci pour votre message !',
      successBody: 'Je vous recontacte dès que possible.',
      again: 'Envoyer un autre message',
      errorRequired: 'Ce champ est obligatoire',
      errorEmail: 'Veuillez saisir une adresse e-mail valide',
      errorSubmit: 'Une erreur est survenue. Veuillez réessayer.',
      basedIn: 'Basée à',
      linkedinCta: 'Me contacter',
    },
  },

  es: {
    nav: { cv: 'C.V.', portfolio: 'Portfolio', contact: 'Contacto' },
    menuOpen: 'Abrir menú',
    menuClose: 'Cerrar menú',
    languageLabel: 'Elegir idioma',
    hero: {
      portfolioCta: 'Ver portfolio',
      contactCta: 'Contactar',
      portraitAlt: 'Retrato de Fauve Willems',
    },
    sections: {
      about: 'Sobre mí',
      features: 'Características',
      skills: 'Habilidades',
      experience: 'Experiencia',
      education: 'Formación',
      contact: 'Contacto',
      photos: 'Fotos',
      reels: 'Reels',
      snippets: 'Fragmentos de vídeo',
    },
    portfolio: {
      eyebrow: 'Portfolio',
      title: 'Fotos, reels y vídeo',
      intro:
        'Una selección de fotos de casting, reels de danza y canto, y fragmentos de producciones. Para vídeos completos o material específico para una audición, envíame un mensaje.',
      filterLabel: 'Filtrar fotos',
      reelsIntro: 'Reels verticales breves para casting: danza, canto e interpretación.',
      closeLabel: 'Cerrar',
    },
    form: {
      intro:
        '¿Te interesa una colaboración, una audición o un taller? Escríbeme por correo electrónico, LinkedIn o Instagram y te responderé lo antes posible.',
      name: 'Nombre',
      email: 'Correo electrónico',
      subject: 'Asunto',
      message: 'Mensaje',
      submit: 'Enviar mensaje',
      sending: 'Enviando…',
      successTitle: '¡Gracias por tu mensaje!',
      successBody: 'Te responderé lo antes posible.',
      again: 'Enviar otro mensaje',
      errorRequired: 'Este campo es obligatorio',
      errorEmail: 'Introduce un correo electrónico válido',
      errorSubmit: 'Algo ha salido mal. Inténtalo de nuevo.',
      basedIn: 'Con base en',
      linkedinCta: 'Contactar',
    },
  },
}
